const totalPax = (travellers) => {
  const t = travellers || {};
  return (
    (t.indianAdults || 0) +
    (t.foreignAdults || 0) +
    (t.childrenWithBed || 0) +
    (t.childrenNoBed || 0)
  );
};

const normalizeCity = (city) => (city || '').trim();

const sameCity = (a, b) =>
  normalizeCity(a).localeCompare(normalizeCity(b), undefined, { sensitivity: 'accent' }) === 0;

const hotelsForCity = (hotels, city) =>
  (hotels || []).filter((h) => sameCity(h.city, city));

/** Resolve selected hotel for an overnight city (per-city index, legacy global index, or name). */
const resolveHotelForCity = (hotels, city, selectedId) => {
  const cityHotels = hotelsForCity(hotels, city);
  if (!cityHotels.length) return null;
  if (selectedId == null || selectedId === '') return cityHotels[0];

  const idStr = String(selectedId);
  const cityIdx = Number(selectedId);
  if (
    Number.isInteger(cityIdx) &&
    cityIdx >= 0 &&
    cityIdx < cityHotels.length &&
    idStr === String(cityIdx)
  ) {
    return cityHotels[cityIdx];
  }

  const globalIdx = Number(selectedId);
  const globalHotel = Number.isInteger(globalIdx) ? hotels[globalIdx] : null;
  if (globalHotel && sameCity(globalHotel.city, city)) return globalHotel;

  return (
    cityHotels.find(
      (h) => h.hotelName === selectedId || (h.id != null && String(h.id) === idStr)
    ) || cityHotels[0]
  );
};

const nightsByCity = (days) => {
  const map = {};
  (days || []).forEach((d) => {
    const city = normalizeCity(d.overnightCity || d.city);
    if (city) {
      map[city] = (map[city] || 0) + 1;
    }
  });
  return map;
};

const hotelChargeOneNight = (hotel, travellers, roomsOverride) => {
  if (!hotel?.roomConfigs?.length) return 0;
  const pricingMethod = hotel.pricingMethod || 'room';
  const nationalityPricing = hotel.nationalityPricing || 'shared';
  const rooms = roomsOverride || [{ roomType: hotel.roomConfigs[0]?.roomType || 'Double', count: 1 }];

  if (pricingMethod !== 'room') {
    const alloc = rooms[0] || {};
    const cfg =
      hotel.roomConfigs.find((r) => r.roomType === alloc.roomType) || hotel.roomConfigs[0];
    const adults = (travellers?.indianAdults || 0) + (travellers?.foreignAdults || 0);
    const cwb = travellers?.childrenWithBed || 0;
    const cnb = travellers?.childrenNoBed || 0;
    if (nationalityPricing === 'separate') {
      return (
        (cfg.adultRateIndian || 0) * (travellers?.indianAdults || 0) +
        (cfg.adultRateForeign || 0) * (travellers?.foreignAdults || 0) +
        (cfg.childBedRateIndian || 0) * cwb +
        (cfg.childBedRateForeign || 0) * cwb +
        (cfg.childNoBedRateIndian || 0) * cnb +
        (cfg.childNoBedRateForeign || 0) * cnb
      );
    }
    return (
      (cfg.adultRateShared || cfg.adultRateIndian || 0) * adults +
      (cfg.childBedRateShared || 0) * cwb +
      (cfg.childNoBedRateShared || 0) * cnb
    );
  }

  let total = 0;
  rooms.forEach((alloc) => {
    const cfg = hotel.roomConfigs.find((r) => r.roomType === alloc.roomType) || hotel.roomConfigs[0];
    const count = alloc.count || 1;
    const base =
      nationalityPricing === 'separate'
        ? cfg.adultRateIndian || cfg.adultRateShared || 0
        : cfg.adultRateShared || cfg.adultRateIndian || 0;
    total += base * count;
  });
  return total;
};

const transportCharge = (t, travellers) => {
  if (t.enabled === false || t.isEnabled === false) return 0;
  const pax = totalPax(travellers);
  const cap = Math.max(1, t.capacity || 1);
  const mode = t.nationalityPricing || 'shared';
  const rateShared = t.rateShared ?? t.rateIndian ?? 0;
  const rateIndian = t.rateIndian ?? rateShared;
  const rateForeign = t.rateForeign ?? rateShared;

  if (t.chargeBasis === 'person') {
    if (mode === 'separate') {
      return (
        rateIndian *
          ((travellers?.indianAdults || 0) +
            (travellers?.childrenWithBed || 0) +
            (travellers?.childrenNoBed || 0)) +
        rateForeign *
          ((travellers?.foreignAdults || 0) +
            (travellers?.childrenWithBed || 0) +
            (travellers?.childrenNoBed || 0))
      );
    }
    return rateShared * pax;
  }
  const vehicles = Math.ceil(pax / cap);
  if (mode === 'separate') {
    const indianPax =
      (travellers?.indianAdults || 0) +
      (travellers?.childrenWithBed || 0) +
      (travellers?.childrenNoBed || 0);
    const foreignPax =
      (travellers?.foreignAdults || 0) +
      (travellers?.childrenWithBed || 0) +
      (travellers?.childrenNoBed || 0);
    const vInd = Math.ceil(indianPax / cap) || 0;
    const vFor = Math.ceil(foreignPax / cap) || 0;
    return vInd * rateIndian + vFor * rateForeign;
  }
  return vehicles * rateShared;
};

const serviceCharge = (svc, travellers) => {
  if (svc.enabled === false || svc.isEnabled === false) return 0;
  const mode = svc.nationalityMode || 'shared';
  const adultsIndian = travellers?.indianAdults || 0;
  const adultsForeign = travellers?.foreignAdults || 0;
  const cwb = travellers?.childrenWithBed || 0;
  const cnb = travellers?.childrenNoBed || 0;

  if (mode === 'separate') {
    return (
      (svc.rateIndian || 0) * adultsIndian +
      (svc.rateForeign || 0) * adultsForeign +
      (svc.childRateIndian || 0) * cwb +
      (svc.childRateForeign || 0) * cwb +
      (svc.childRateIndian || 0) * cnb +
      (svc.childRateForeign || 0) * cnb
    );
  }
  const pax = totalPax(travellers);
  const adultRate = svc.rateIndian ?? svc.rateForeign ?? 0;
  const childRate = svc.childRateIndian ?? svc.childRateForeign ?? adultRate;
  return adultRate * (adultsIndian + adultsForeign) + childRate * (cwb + cnb);
};

export const computeItineraryPricing = (data, travellers, bookingState) => {
  const alerts = [];
  const days = data?.days || [];
  const hotels = data?.hotels || [];
  const transports = (data?.transports || []).filter((t) => {
    if (t.enabled === false || t.isEnabled === false) return false;
    if (t.context === 'full_tour' || t.context === 'package') {
      const selected = bookingState?.transportPackageId;
      if (!selected) return false;
      return String(t.id) === String(selected) || t.contextId === selected;
    }
    return true;
  });
  const nightsMap = nightsByCity(days);

  let accommodation = 0;
  const accommodationByCity = {};

  Object.keys(nightsMap).forEach((city) => {
    const nights = nightsMap[city];
    const selectedId = bookingState?.hotelSelection?.[city];
    const hotel = resolveHotelForCity(hotels, city, selectedId);
    if (!hotel) {
      alerts.push(`No hotel configured for ${city}`);
      return;
    }
    const roomAlloc = bookingState?.roomAllocation?.[city];
    const charge = hotelChargeOneNight(hotel, travellers, roomAlloc) * nights;
    accommodationByCity[city] = charge;
    accommodation += charge;
  });

  let transport = 0;
  transports.forEach((t) => {
    transport += transportCharge(t, travellers);
  });

  let services = 0;
  days.forEach((d) => {
    (d.services || []).forEach((svc) => {
      services += serviceCharge(svc, travellers);
    });
  });

  const supplierCost = accommodation + transport + services;
  const markupPct = Number(data?.markupPercent) || 0;
  const markup = supplierCost * (markupPct / 100);
  const subtotal = supplierCost + markup;

  const gstRate = Number(data?.gstRate) || 0;
  const gstMode = data?.gstMode || 'exclusive';
  let gst = 0;
  let total = subtotal;
  if (gstRate > 0) {
    if (gstMode === 'inclusive') {
      gst = subtotal - subtotal / (1 + gstRate / 100);
      total = subtotal;
    } else {
      gst = subtotal * (gstRate / 100);
      total = subtotal + gst;
    }
  }

  const pax = totalPax(travellers);
  if (pax === 0) {
    alerts.push('Add at least one traveller for pricing');
  }

  return {
    pax,
    accommodation,
    accommodationByCity,
    transport,
    services,
    supplierCost,
    markup,
    markupPct,
    gst,
    gstRate,
    gstMode,
    subtotal,
    total,
    perPerson: pax > 0 ? total / pax : 0,
    alerts,
  };
};

export { totalPax, nightsByCity, hotelsForCity, resolveHotelForCity, normalizeCity, sameCity };
